import { helper } from "@ember/component/helper";
import { assert } from "@ember/debug";
import type Patient from "parog-web/models/patient";

import type { ComponentLike } from "@glint/template";
import CardioHeartFailureDiagnostic from "parog-web/components/medical-history/cardio/heart-failure/diagnostic-fields";
import CardioHeartFailurePathology from "parog-web/components/medical-history/cardio/heart-failure/pathology-fields";
import CardioTransplantDiagnostic from "parog-web/components/medical-history/cardio/transplant/diagnostic-fields";
import CardioTransplantPathology from "parog-web/components/medical-history/cardio/transplant/pathology-fields";
import DiabetoDt2Diagnostic from "parog-web/components/medical-history/diabeto/dt2/diagnostic-fields";
import DiabetoDt2MedicalSynthesis from "parog-web/components/medical-history/diabeto/dt2/medical-synthesis-fields";
import DigestCrohnDiagnostic from "parog-web/components/medical-history/digest/crohn/diagnostic-fields";
import DigestRchDiagnostic from "parog-web/components/medical-history/digest/rch/diagnostic-fields";
import HepatoTransplantDiagnostic from "parog-web/components/medical-history/hepato/transplant/diagnostic-fields";
import HepatoTransplantPathology from "parog-web/components/medical-history/hepato/transplant/pathology-fields";
import MentalBipolarDiagnostic from "parog-web/components/medical-history/mental/bipolar/diagnostic-fields";
import MentalBipolarMedicalSynthesis from "parog-web/components/medical-history/mental/bipolar/medical-synthesis-fields";
import MentalDepressionDiagnostic from "parog-web/components/medical-history/mental/depression/diagnostic-fields";
import NephroDonorDiagnostic from "parog-web/components/medical-history/nephro/donor/diagnostic-fields";
import NephroGenericDiagnostic from "parog-web/components/medical-history/nephro/generic/diagnostic-fields";
import NephroMrcDiagnostic from "parog-web/components/medical-history/nephro/mrc/diagnostic-fields";
import NephroMrcPathology from "parog-web/components/medical-history/nephro/mrc/pathology-fields";
import NephroMrcPreTransplantAssessmentResults from "parog-web/components/medical-history/nephro/mrc/pre-transplant-assessment-results-fields";
import NephroMrcSocialSituation from "parog-web/components/medical-history/nephro/mrc/social-situation-fields";
import NephroNotDonorDiagnostic from "parog-web/components/medical-history/nephro/not-donor/diagnostic-fields";
import NephroNotDonorPathology from "parog-web/components/medical-history/nephro/not-donor/pathology-fields";
import NephroTransplantDiagnostic from "parog-web/components/medical-history/nephro/transplant/diagnostic-fields";
import NephroTransplantPathology from "parog-web/components/medical-history/nephro/transplant/pathology-fields";
import OncoBaseAntecedents from "parog-web/components/medical-history/onco/base-antecedents-fields";
import OncoBrainCancerAntecedents from "parog-web/components/medical-history/onco/brain-cancer/antecedents-fields";
import OncoBrainCancerDiagnostic from "parog-web/components/medical-history/onco/brain-cancer/diagnostic-fields";
import OncoBreastCancerTnm0Antecedents from "parog-web/components/medical-history/onco/breast-cancer-tnm0/antecedents-fields";
import OncoBreastCancerTnm0Diagnostic from "parog-web/components/medical-history/onco/breast-cancer-tnm0/diagnostic-fields";
import OncoBreastCancerTnm0Pathology from "parog-web/components/medical-history/onco/breast-cancer-tnm0/pathology-fields";
import OncoBreastCancerAntecedents from "parog-web/components/medical-history/onco/breast-cancer/antecedents-fields";
import OncoBreastCancerDiagnostic from "parog-web/components/medical-history/onco/breast-cancer/diagnostic-fields";
import OncoBreastCancerPathology from "parog-web/components/medical-history/onco/breast-cancer/pathology-fields";
import OncoCervixCancerAntecedents from "parog-web/components/medical-history/onco/cervix-cancer/antecedents-fields";
import OncoCervixCancerDiagnostic from "parog-web/components/medical-history/onco/cervix-cancer/diagnostic-fields";
import OncoColonCancerAntecedents from "parog-web/components/medical-history/onco/colon-cancer/antecedents-fields";
import OncoColonCancerDiagnostic from "parog-web/components/medical-history/onco/colon-cancer/diagnostic-fields";
import OncoHematoCancerAntecedents from "parog-web/components/medical-history/onco/hemato-cancer/antecedents-fields";
import OncoHematoCancerDiagnostic from "parog-web/components/medical-history/onco/hemato-cancer/diagnostic-fields";
import OncoKidneyCancerAntecedents from "parog-web/components/medical-history/onco/kidney-cancer/antecedents-fields";
import OncoKidneyCancerDiagnostic from "parog-web/components/medical-history/onco/kidney-cancer/diagnostic-fields";
import OncoLiverCancerAntecedents from "parog-web/components/medical-history/onco/liver-cancer/antecedents-fields";
import OncoLiverCancerDiagnostic from "parog-web/components/medical-history/onco/liver-cancer/diagnostic-fields";
import OncoLungCancerAntecedents from "parog-web/components/medical-history/onco/lung-cancer/antecedents-fields";
import OncoLungCancerDiagnostic from "parog-web/components/medical-history/onco/lung-cancer/diagnostic-fields";
import OncoLungCancerPathology from "parog-web/components/medical-history/onco/lung-cancer/pathology-fields";
import OncoLymphomaCancerAntecedents from "parog-web/components/medical-history/onco/lymphoma-cancer/antecedents-fields";
import OncoLymphomaCancerDiagnostic from "parog-web/components/medical-history/onco/lymphoma-cancer/diagnostic-fields";
import OncoMyelomaCancerAntecedents from "parog-web/components/medical-history/onco/myeloma-cancer/antecedents-fields";
import OncoMyelomaCancerDiagnostic from "parog-web/components/medical-history/onco/myeloma-cancer/diagnostic-fields";
import OncoOvarianCancerAntecedents from "parog-web/components/medical-history/onco/ovarian-cancer/antecedents-fields";
import OncoOvarianCancerDiagnostic from "parog-web/components/medical-history/onco/ovarian-cancer/diagnostic-fields";
import OncoPancreaticCancerAntecedents from "parog-web/components/medical-history/onco/pancreatic-cancer/antecedents-fields";
import OncoPancreaticCancerDiagnostic from "parog-web/components/medical-history/onco/pancreatic-cancer/diagnostic-fields";
import OncoPancreaticCancerPathology from "parog-web/components/medical-history/onco/pancreatic-cancer/pathology-fields";
import OncoProstateCancerAntecedents from "parog-web/components/medical-history/onco/prostate-cancer/antecedents-fields";
import OncoProstateCancerDiagnostic from "parog-web/components/medical-history/onco/prostate-cancer/diagnostic-fields";
import OncoProstateCancerPathology from "parog-web/components/medical-history/onco/prostate-cancer/pathology-fields";
import OncoSkinCancerAntecedents from "parog-web/components/medical-history/onco/skin-cancer/antecedents-fields";
import OncoSkinCancerDiagnostic from "parog-web/components/medical-history/onco/skin-cancer/diagnostic-fields";
import OncoStomachCancerAntecedents from "parog-web/components/medical-history/onco/stomach-cancer/antecedents-fields";
import OncoStomachCancerDiagnostic from "parog-web/components/medical-history/onco/stomach-cancer/diagnostic-fields";
import OncoTesticularCancerAntecedents from "parog-web/components/medical-history/onco/testicular-cancer/antecedents-fields";
import OncoTesticularCancerDiagnostic from "parog-web/components/medical-history/onco/testicular-cancer/diagnostic-fields";
import PneumoTransplantDiagnostic from "parog-web/components/medical-history/pneumo/transplant/diagnostic-fields";
import PneumoTransplantPathology from "parog-web/components/medical-history/pneumo/transplant/pathology-fields";
import RhumatoPrDiagnostic from "parog-web/components/medical-history/rhumato/pr/diagnostic-fields";
import RhumatoPrPathology from "parog-web/components/medical-history/rhumato/pr/pathology-fields";

const FieldComponents = {
  "nephro/not-donor/pathology": NephroNotDonorPathology,
  "nephro/not-donor/diagnostic": NephroNotDonorDiagnostic,
  "nephro/mrc/pathology": NephroMrcPathology,
  "nephro/mrc/diagnostic": NephroMrcDiagnostic,
  "nephro/mrc/social-situation": NephroMrcSocialSituation,
  "nephro/mrc/pre-transplant-assessment-results":
    NephroMrcPreTransplantAssessmentResults,
  "nephro/transplant/pathology": NephroTransplantPathology,
  "nephro/transplant/diagnostic": NephroTransplantDiagnostic,
  "nephro/donor/diagnostic": NephroDonorDiagnostic,
  "nephro/generic/diagnostic": NephroGenericDiagnostic,
  "diabeto/dt2/diagnostic": DiabetoDt2Diagnostic,
  "diabeto/dt2/medical-synthesis": DiabetoDt2MedicalSynthesis,
  "cardio/heart-failure/pathology": CardioHeartFailurePathology,
  "cardio/heart-failure/diagnostic": CardioHeartFailureDiagnostic,
  "cardio/transplant/pathology": CardioTransplantPathology,
  "cardio/transplant/diagnostic": CardioTransplantDiagnostic,
  "mental/bipolar/diagnostic": MentalBipolarDiagnostic,
  "mental/bipolar/medical-synthesis": MentalBipolarMedicalSynthesis,
  "mental/depression/diagnostic": MentalDepressionDiagnostic,
  "digest/crohn/diagnostic": DigestCrohnDiagnostic,
  "digest/rch/diagnostic": DigestRchDiagnostic,
  "pneumo/transplant/pathology": PneumoTransplantPathology,
  "pneumo/transplant/diagnostic": PneumoTransplantDiagnostic,
  "hepato/transplant/pathology": HepatoTransplantPathology,
  "hepato/transplant/diagnostic": HepatoTransplantDiagnostic,
  "onco/pancreatic-cancer/pathology": OncoPancreaticCancerPathology,
  "onco/pancreatic-cancer/antecedents": OncoPancreaticCancerAntecedents,
  "onco/pancreatic-cancer/diagnostic": OncoPancreaticCancerDiagnostic,
  "onco/colon-cancer/antecedents": OncoColonCancerAntecedents,
  "onco/colon-cancer/diagnostic": OncoColonCancerDiagnostic,
  "onco/kidney-cancer/antecedents": OncoKidneyCancerAntecedents,
  "onco/kidney-cancer/diagnostic": OncoKidneyCancerDiagnostic,
  "onco/base-antecedents": OncoBaseAntecedents,
  "onco/skin-cancer/antecedents": OncoSkinCancerAntecedents,
  "onco/skin-cancer/diagnostic": OncoSkinCancerDiagnostic,
  "onco/ovarian-cancer/antecedents": OncoOvarianCancerAntecedents,
  "onco/ovarian-cancer/diagnostic": OncoOvarianCancerDiagnostic,
  "onco/breast-cancer-tnm0/pathology": OncoBreastCancerTnm0Pathology,
  "onco/breast-cancer-tnm0/antecedents": OncoBreastCancerTnm0Antecedents,
  "onco/breast-cancer-tnm0/diagnostic": OncoBreastCancerTnm0Diagnostic,
  "onco/stomach-cancer/antecedents": OncoStomachCancerAntecedents,
  "onco/stomach-cancer/diagnostic": OncoStomachCancerDiagnostic,
  "onco/breast-cancer/pathology": OncoBreastCancerPathology,
  "onco/breast-cancer/antecedents": OncoBreastCancerAntecedents,
  "onco/breast-cancer/diagnostic": OncoBreastCancerDiagnostic,
  "onco/testicular-cancer/antecedents": OncoTesticularCancerAntecedents,
  "onco/testicular-cancer/diagnostic": OncoTesticularCancerDiagnostic,
  "onco/lung-cancer/pathology": OncoLungCancerPathology,
  "onco/lung-cancer/antecedents": OncoLungCancerAntecedents,
  "onco/lung-cancer/diagnostic": OncoLungCancerDiagnostic,
  "onco/prostate-cancer/pathology": OncoProstateCancerPathology,
  "onco/prostate-cancer/antecedents": OncoProstateCancerAntecedents,
  "onco/prostate-cancer/diagnostic": OncoProstateCancerDiagnostic,
  "onco/myeloma-cancer/antecedents": OncoMyelomaCancerAntecedents,
  "onco/myeloma-cancer/diagnostic": OncoMyelomaCancerDiagnostic,
  "onco/hemato-cancer/antecedents": OncoHematoCancerAntecedents,
  "onco/hemato-cancer/diagnostic": OncoHematoCancerDiagnostic,
  "onco/brain-cancer/antecedents": OncoBrainCancerAntecedents,
  "onco/brain-cancer/diagnostic": OncoBrainCancerDiagnostic,
  "onco/liver-cancer/antecedents": OncoLiverCancerAntecedents,
  "onco/liver-cancer/diagnostic": OncoLiverCancerDiagnostic,
  "onco/lymphoma-cancer/antecedents": OncoLymphomaCancerAntecedents,
  "onco/lymphoma-cancer/diagnostic": OncoLymphomaCancerDiagnostic,
  "onco/cervix-cancer/antecedents": OncoCervixCancerAntecedents,
  "onco/cervix-cancer/diagnostic": OncoCervixCancerDiagnostic,
  "rhumato/pr/pathology": RhumatoPrPathology,
  "rhumato/pr/diagnostic": RhumatoPrDiagnostic
};

interface FieldComponentSignature {
  Args: {
    forcComponent?: unknown;
    patient: Patient;
    form: unknown;
  };
}

function medicalHistoryFieldsComponent([patient, section]: [
  patient: Patient,
  section: string
]): ComponentLike<FieldComponentSignature> | undefined {
  const fur = patient.followUpReasonRecord;
  if (!fur) return;

  const list = [
    "diagnostic",
    "pathology",
    "antecedents",
    "pre-transplant-assessment-results",
    "social-situation"
  ];

  assert(
    `You must pass a section param which is ${list.join(", ")}. You passed ${section}`,
    list.includes(section)
  );

  return FieldComponents[`${fur.therapeuticalArea.id}/${fur.patho}/${section}`];
}

export default helper(medicalHistoryFieldsComponent);
